import { useOrganization as useClerkOrganization } from '@clerk/react';
import { RouterProvider, useRouterState } from '@tanstack/react-router';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { useAiV2Enabled } from 'src/hooks/useAiV2Enabled';
import { useIdentifyClarityUser } from 'src/lib/clarity';
import { FeatureFlagsClient } from 'src/lib/constants';
import { useIdentifyFaroUser } from 'src/lib/grafana';
import { initJamMetadata } from 'src/lib/jam';
import { useInitializeOrgSettings } from 'src/modules/settings/orgSettings.store';
import { useAuth, useIsCustomer, useIsDawsonsWarehouseUser, useIsMember } from '../auth';
import { navigateHref } from '../lib/routing';
import { canonicalRouteSpecs } from './routeCatalog';
import { type AppRouter, getAppRouter } from './router';
import { type AppRouterContext, resolveSignedOutLoginHref } from './routerAccess';

const IS_E2E = process.env.AGRIFUL_ENV === 'e2e' || process.env.E2E_MODE === 'true';
const EMPTY_ROLES: readonly string[] = [];

const useOrganization = IS_E2E
  ? () => ({ organization: { id: 'org_e2e_test', name: 'E2E Test Org' } })
  : useClerkOrganization;

// The app's single signed-out redirect. Every route reaches this one function:
// unmatched URLs, /scan-debug and /login (no beforeLoad, no staticData.access),
// and framed routes whose enforceRouteAccess already collapsed them to NotFound.
function resolveSignedOutRedirect(router: AppRouter): string | undefined {
  const currentLocation = router.history.location;
  if (currentLocation.pathname === canonicalRouteSpecs.login.path) return undefined;

  const parsedLocation = router.parseLocation(currentLocation);
  const [, rawParams, foundRoute] = router.getMatchedRoutes(parsedLocation.pathname);
  const routeAccess = rawParams['**'] === undefined ? foundRoute?.options.staticData?.access : undefined;

  return resolveSignedOutLoginHref(router.options.context, routeAccess, currentLocation.href);
}

export default function RoutesBridge() {
  return <RoutesBridgeWithRouter isE2E={IS_E2E} router={getAppRouter()} />;
}

type RoutesBridgeWithRouterProps = Readonly<{ isE2E: boolean; router: AppRouter }>;

function RoutesBridgeWithRouter({ isE2E, router }: RoutesBridgeWithRouterProps) {
  const { currentUser, isAuthenticated, loading } = useAuth();
  const { organization } = useOrganization();
  const ldClient = useLDClient();
  const flags = useFlags();
  const rawIsMember = useIsMember();
  const rawIsCustomer = useIsCustomer();
  const isMember = isE2E || Boolean(rawIsMember);
  const isCustomer = !isE2E && Boolean(rawIsCustomer);
  const isDawsonsWarehouseUser = Boolean(useIsDawsonsWarehouseUser());
  const aiV2 = useAiV2Enabled();
  const automationSettings = Boolean(flags[FeatureFlagsClient.AutomationSettings]);
  const roles = currentUser?.roles ?? EMPTY_ROLES;
  const routerContext: AppRouterContext = {
    auth: { isAuthenticated, isCustomer, isDawsonsWarehouseUser, isMember, loading, roles },
    featureFlags: { automationSettings, aiV2 },
    isE2E,
  };
  const routerContextKey = JSON.stringify(routerContext);
  const previousRouterContextKey = useRef<string | undefined>(undefined);

  useIdentifyClarityUser(organization);
  useIdentifyFaroUser(organization);
  useInitializeOrgSettings();

  useLayoutEffect(() => {
    if (previousRouterContextKey.current === routerContextKey) return;
    previousRouterContextKey.current = routerContextKey;
    void router.invalidate({ sync: true });
  }, [router, routerContextKey]);

  useEffect(() => {
    initJamMetadata(() => currentUser, organization?.name);
  }, [currentUser, organization?.name]);

  useEffect(() => {
    if (!currentUser || !organization) return;

    const context = {
      kind: 'multi',
      user: {
        key: currentUser.id,
        name: `${currentUser.firstName} ${currentUser.lastName}`,
        email: Array.isArray(currentUser.emailAddress) ? currentUser.emailAddress[0] : currentUser.emailAddress,
      },
      organization: { key: organization.id, name: organization.name },
    };

    ldClient?.identify(context, undefined, (error) => {
      if (error) {
        console.error(`[Launch Darkly] Unable to set up context. Error: ${error}`);
        return;
      }
      console.log(
        '[Launch Darkly] Set up new user context for user',
        `${currentUser.firstName} ${currentUser.lastName}`,
        'and organization',
        organization.name,
        'successfully.'
      );
    });
  }, [ldClient, currentUser, organization]);

  // Read outside the RouterProvider below, so `router` is passed explicitly.
  // `undefined` while the redirect is off: an authenticated visitor's navigation
  // must not re-render this auth setup.
  const signedOutPublicHref = useRouterState({
    router,
    select: (state) => (isE2E || isAuthenticated || loading ? undefined : state.location.publicHref),
  });

  useEffect(() => {
    if (signedOutPublicHref === undefined) return;

    const loginHref = resolveSignedOutRedirect(router);
    if (loginHref !== undefined) void navigateHref(router, loginHref);
  }, [router, signedOutPublicHref]);

  return <RouterProvider context={routerContext} router={router} />;
}
